import React, { useState } from 'react';
import { BarChart3, MessageSquare, Heart, TrendingUp, Menu, X, CheckCircle, DollarSign, ArrowRight, Shield, Calculator, Download, Mail, Calendar, FileText, Clock } from 'lucide-react';

// Export utility functions
const downloadPDF = (results) => {
  const reportContent = `
AIM TRUST COMPARISON REPORT
${results.municipality}
Generated: ${new Date().toLocaleDateString()}

CURRENT SITUATION:
• Current AIM Cost: $${results.aimCost.toLocaleString()}/year
• Cost per employee: $${results.totalPremium}/month

MARKET ALTERNATIVE:
• Estimated Market Cost: $${results.marketCost.toLocaleString()}/year
• Cost per employee: $${results.marketPremium}/month

POTENTIAL SAVINGS:
• Annual Savings: $${results.savings.toLocaleString()}
• Monthly Savings: $${results.monthlySavings.toLocaleString()}
• Percentage Reduction: ${results.savingsPercent}%

RECOMMENDATION:
${results.recommendation}

Generated by Mark - From Hire to Retire
  `;

  const blob = new Blob([reportContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${results.municipality.replace(/\s+/g, '-')}-AIM-Analysis.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const emailReport = (results) => {
  const subject = encodeURIComponent(`AIM Trust Analysis - ${results.municipality}`);
  const body = encodeURIComponent(`Hi,

I've completed an analysis of our current AIM Trust costs vs. market alternatives.

KEY FINDINGS:
• Current Annual Cost: $${results.aimCost.toLocaleString()}
• Estimated Market Cost: $${results.marketCost.toLocaleString()}
• Potential Annual Savings: $${results.savings.toLocaleString()} (${results.savingsPercent}% reduction)

${results.recommendation}

Let's schedule a time to discuss this opportunity.

Best regards`);

  // Try Gmail first, fallback to default email client
  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
  const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
  
  // Open Gmail in new tab
  const win = window.open(gmailUrl, '_blank');
  
  // Fallback to mailto if popup blocked
  if (!win) {
    window.location.href = mailtoUrl;
  }
};

const scheduleWebinarMeeting = (results) => {
  const title = encodeURIComponent(`Benefits Review: AIM Trust Analysis - ${results.municipality}`);
  const details = encodeURIComponent(`Discuss potential savings of $${results.savings.toLocaleString()}/year by transitioning from AIM Trust to market alternatives.

Key Discussion Points:
• Current costs vs. market rates
• Implementation timeline
• Employee communication strategy
• Plan design options

Analysis shows ${results.savingsPercent}% potential cost reduction.`);
  
  // Google Calendar
  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 7); // Schedule 1 week out
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 1);
  
  const formatDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '');
  };
  
  const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&dates=${formatDate(startDate)}/${formatDate(endDate)}`;
  
  // Outlook Calendar
  const outlookCalUrl = `https://outlook.office.com/calendar/0/deeplink/compose?subject=${title}&body=${details}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}`;
  
  // Prompt user to choose
  const useGoogle = window.confirm('Create event in Google Calendar? (Click Cancel for Outlook)');
  window.open(useGoogle ? googleCalUrl : outlookCalUrl, '_blank');
};

const MarkApp = () => {
  const [activeTab, setActiveTab] = useState('landing');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabs = [
    { id: 'landing', name: 'Home', icon: Shield },
    { id: 'chat', name: 'Ask Mark', icon: MessageSquare },
    { id: 'analyzer', name: 'Cost Analyzer', icon: BarChart3 },
    { id: 'aim', name: 'Market Comparison', icon: TrendingUp },
    { id: 'budget', name: 'Budget Forecast', icon: DollarSign },
    { id: 'rfp', name: 'RFP Generator', icon: FileText },
    { id: 'enrollment', name: 'Enrollment Timeline', icon: Clock },
    { id: 'medicare', name: 'Medicare', icon: Heart },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-900 rounded-lg p-3 shadow-lg cursor-pointer" onClick={() => setActiveTab('landing')}>
                <div className="text-center">
                  <div className="text-3xl font-black text-blue-400 tracking-wider">MARK</div>
                  <div className="text-xs font-semibold text-yellow-400 tracking-wider">FROM HIRE TO RETIRE</div>
                </div>
              </div>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden">
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          <nav className={`lg:col-span-3 ${mobileMenuOpen ? 'block' : 'hidden'} lg:block mb-8`}>
            <div className="bg-white rounded-lg shadow-sm p-4 space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button key={tab.id} onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.name}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          <main className="lg:col-span-9">
            {activeTab === 'landing' && <LandingPage setActiveTab={setActiveTab} />}
            {activeTab === 'chat' && <MarkChatbot />}
            {activeTab === 'analyzer' && <CostAnalyzer />}
            {activeTab === 'aim' && <AIMComparison />}
            {activeTab === 'budget' && <BudgetForecast />}
            {activeTab === 'rfp' && <RFPGenerator />}
            {activeTab === 'enrollment' && <EnrollmentTimeline />}
            {activeTab === 'medicare' && <MedicareTransition />}
          </main>
        </div>
      </div>
    </div>
  );
};

const LandingPage = ({ setActiveTab }) => {
  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-800 rounded-2xl shadow-xl p-8 md:p-12 text-white">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Optimize Your Municipality's <span className="text-yellow-400">Benefits Strategy</span>
        </h1>
        <p className="text-xl md:text-2xl mb-6 text-blue-100">
          Mark helps Indiana municipalities make data-driven decisions about employee benefits—from hire to retire.
        </p>
        <button 
          onClick={() => setActiveTab('aim')}
          className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-colors flex items-center space-x-2"
        >
          <span>Analyze Your Benefits</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* The Opportunity */}
      <div className="bg-white rounded-xl shadow-sm p-8">
        <h2 className="text-3xl font-bold mb-6 text-gray-900">The Benefits Optimization Opportunity</h2>
        <div className="space-y-4 text-lg text-gray-700">
          <p>
            Municipal benefits programs haven't always kept pace with the changing insurance marketplace. Many municipalities stay with legacy providers simply because "that's how it's always been done."
          </p>
          <p className="font-semibold text-indigo-600">
            Based on market analysis, comparing your current costs to competitive alternatives can reveal significant savings opportunities—often 10-15% annually.
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-6">
            <p className="font-bold text-blue-900 text-xl">
              For a 500-employee municipality, even a 12% cost reduction represents $500K+ in annual savings.
            </p>
          </div>
          <p>
            Mark provides the data and analysis tools you need to make informed decisions about your benefits strategy—whether that means optimizing your current plan or exploring alternatives.
          </p>
        </div>
      </div>

      {/* Real Data Example */}
      <div className="bg-indigo-50 rounded-xl border-2 border-indigo-200 p-8">
        <h3 className="text-2xl font-bold mb-4 text-indigo-900">Real Indiana Municipality Example</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-2">Municipality Size</p>
            <p className="text-3xl font-bold text-gray-900">514 Employees</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Potential Annual Savings</p>
            <p className="text-3xl font-bold text-green-600">$600K - $850K</p>
          </div>
        </div>
        <div className="mt-6 p-4 bg-white rounded-lg">
          <p className="text-gray-700">
            <strong>Current approach:</strong> Traditional group health coverage with full employer contribution for employee-only plans, family coverage with employee contribution.
          </p>
          <p className="text-gray-700 mt-2">
            <strong>Market comparison:</strong> Analyzing competitive options revealed potential savings of 12-15% while maintaining comparable coverage levels.
          </p>
          <p className="text-gray-700 mt-2 text-sm">
            <em>Results will vary by municipality size, demographics, and plan design. Mark provides customized analysis for your specific situation.</em>
          </p>
        </div>
      </div>

      {/* What Mark Does */}
      <div className="bg-white rounded-xl shadow-sm p-8">
        <h2 className="text-3xl font-bold mb-6 text-gray-900">What Mark Does</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center p-6">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calculator className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="font-bold text-xl mb-2">Analyze Your Costs</h3>
            <p className="text-gray-600">Input your current AIM premiums and employee count. Mark calculates your total exposure.</p>
          </div>
          <div className="text-center p-6">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-bold text-xl mb-2">Compare to Market</h3>
            <p className="text-gray-600">See exactly how much you'd save with competitive market rates—based on real Indiana data.</p>
          </div>
          <div className="text-center p-6">
            <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-indigo-600" />
            </div>
            <h3 className="font-bold text-xl mb-2">Ask Mark Anything</h3>
            <p className="text-gray-600">Chat with Mark's AI assistant to learn about benefits, insurance strategies, and cost optimization.</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl shadow-xl p-8 text-center text-white">
        <h2 className="text-3xl font-bold mb-4">Ready to Optimize Your Benefits?</h2>
        <p className="text-xl mb-6 text-blue-100">
          Get a comprehensive analysis of your current costs and explore opportunities for improvement.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={() => setActiveTab('aim')}
            className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center space-x-2"
          >
            <span>Compare Your Costs</span>
            <ArrowRight className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setActiveTab('chat')}
            className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-colors inline-flex items-center justify-center space-x-2"
          >
            <MessageSquare className="w-5 h-5" />
            <span>Ask Mark</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const MarkChatbot = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hey there! I'm Mark, your benefits expert. I help Indiana municipalities understand insurance, optimize costs, and make smart benefits decisions from hire to retire. What would you like to know? (Try asking about market comparisons, Medicare transitions, HSAs, or any benefits topic!)"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: `You are Mark, an enthusiastic and knowledgeable benefits consultant specializing in municipal employee benefits in Indiana. Your expertise includes:

- Benefits market analysis and cost comparison
- Medicare transitions and Medigap plan selection
- HSA strategies and contribution limits
- Benefits cost optimization for municipalities
- Indiana-specific insurance regulations and benchmarks
- Group health insurance plan design

Your personality:
- Friendly, approachable, and use casual language
- End responses with "Mark it, dude!" when giving strong recommendations
- Use real numbers and data when possible (e.g., "typical municipal health costs run $900-1,100/employee/month")
- Focus on practical, actionable advice
- Care deeply about helping municipalities save money while maintaining quality benefits
- Stay neutral when discussing specific providers - focus on helping municipalities make informed decisions

Keep responses concise but informative. If asked about specific calculations, encourage them to use the calculators in the app.`,
          messages: messages.concat(userMessage)
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to get response');
      }

      const assistantMessage = {
        role: 'assistant',
        content: data.content[0].text
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Oops! I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "How can we reduce our benefits costs?",
    "How does Medicare transition work?",
    "What's a good HSA contribution?",
    "What should I look for when comparing plans?"
  ];

  const askSuggested = (question) => {
    setInput(question);
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-blue-900 to-indigo-800 rounded-xl shadow-sm p-6 text-white">
        <div className="flex items-center space-x-3 mb-2">
          <MessageSquare className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Ask Mark Anything</h2>
        </div>
        <p className="text-blue-100">Your AI benefits consultant, powered by real Indiana municipal data</p>
      </div>

      {messages.length === 1 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-semibold mb-3 text-gray-900">Popular Questions:</h3>
          <div className="grid md:grid-cols-2 gap-2">
            {suggestedQuestions.map((q, idx) => (
              <button
                key={idx}
                onClick={() => askSuggested(q)}
                className="text-left px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm text-blue-900 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-4 mb-4 max-h-[500px] overflow-y-auto">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg p-4 ${
                msg.role === 'user' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-100 text-gray-900'
              }`}>
                {msg.role === 'assistant' && idx === 0 && (
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="bg-blue-900 rounded px-2 py-1">
                      <span className="text-xs font-bold text-blue-400">MARK</span>
                    </div>
                  </div>
                )}
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Mark about benefits, insurance, or cost savings..."
            className="flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !input.trim()}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

const CostAnalyzer = () => {
  const [data, setData] = useState({ employees: '', avgPremium: '' });
  const [results, setResults] = useState(null);

  const analyze = () => {
    const emp = parseInt(data.employees) || 45;
    const prem = parseInt(data.avgPremium) || 1200;
    setResults({
      totalAnnualCost: prem * emp * 12,
      perEmployeeCost: prem * 12,
      assessment: 'Based on Indiana benchmarks, your costs are approximately 8% above average.'
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold mb-4">Benefits Cost Analyzer</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">Number of Employees</label>
            <input type="number" value={data.employees} onChange={(e) => setData({...data, employees: e.target.value})} placeholder="45" className="w-full px-3 py-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Avg Monthly Premium</label>
            <input type="number" value={data.avgPremium} onChange={(e) => setData({...data, avgPremium: e.target.value})} placeholder="1200" className="w-full px-3 py-2 border rounded-md" />
          </div>
        </div>
        <button onClick={analyze} className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700">Analyze Costs</button>
      </div>
      {results && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-3">Results</h3>
          <p className="text-3xl font-bold mb-2">${results.totalAnnualCost.toLocaleString()}</p>
          <p className="text-gray-600">{results.assessment}</p>
        </div>
      )}
    </div>
  );
};

const AIMComparison = () => {
  const [data, setData] = useState({ 
    municipality: '', 
    employees: '', 
    employeePremium: '',
    employerPremium: '',
    useTotal: true
  });
  const [results, setResults] = useState(null);

  const getEmployeeCount = () => parseInt(data.employees) || 500;
  
  const getSuggestedRange = () => {
    const emp = getEmployeeCount();
    if (emp < 100) return { low: 850, high: 1000 };
    if (emp < 300) return { low: 900, high: 1050 };
    return { low: 900, high: 1100 };
  };

  const compare = () => {
    const emp = getEmployeeCount();
    const employeePrem = parseFloat(data.employeePremium) || 0;
    const employerPrem = parseFloat(data.employerPremium) || 0;
    
    const totalPremium = employeePrem + employerPrem || 1000;
    const marketPrem = Math.round(totalPremium * 0.87); // 13% savings
    const savings = (totalPremium - marketPrem) * emp * 12;
    const monthlySavings = (totalPremium - marketPrem) * emp;

    setResults({
      municipality: data.municipality || 'Your Municipality',
      aimCost: totalPremium * emp * 12,
      marketCost: marketPrem * emp * 12,
      savings: savings,
      monthlySavings: monthlySavings,
      totalPremium: totalPremium,
      marketPremium: marketPrem,
      savingsPercent: Math.round(((totalPremium - marketPrem) / totalPremium) * 100),
      recommendation: `Based on actual Indiana market data, exploring competitive alternatives could save ${data.municipality || 'your municipality'} approximately $${savings.toLocaleString()} annually. That's $${monthlySavings.toLocaleString()} every month. Mark it, dude!`
    });
  };

  const range = getSuggestedRange();

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold mb-4">Benefits Market Comparison</h2>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-900">
            <strong>Typical municipal health benefits costs in Indiana:</strong> ${range.low}-${range.high}/employee/month
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">Municipality Name</label>
            <input 
              type="text" 
              value={data.municipality} 
              onChange={(e) => setData({...data, municipality: e.target.value})} 
              placeholder="City of Fishers" 
              className="w-full px-3 py-2 border rounded-md" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Number of Employees</label>
            <input 
              type="number" 
              value={data.employees} 
              onChange={(e) => setData({...data, employees: e.target.value})} 
              placeholder="500" 
              className="w-full px-3 py-2 border rounded-md" 
            />
          </div>
        </div>

        <div className="border-t pt-4 mb-4">
          <h3 className="font-semibold mb-3">Premium Breakdown (per employee/month)</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Employee Pays (Monthly)</label>
              <input 
                type="number" 
                value={data.employeePremium} 
                onChange={(e) => setData({...data, employeePremium: e.target.value})} 
                placeholder="85" 
                className="w-full px-3 py-2 border rounded-md" 
              />
              <p className="text-xs text-gray-500 mt-1">Average employee contribution</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Employer Pays (Monthly)</label>
              <input 
                type="number" 
                value={data.employerPremium} 
                onChange={(e) => setData({...data, employerPremium: e.target.value})} 
                placeholder="915" 
                className="w-full px-3 py-2 border rounded-md" 
              />
              <p className="text-xs text-gray-500 mt-1">Your municipality's cost per employee</p>
            </div>
          </div>
          {(data.employeePremium || data.employerPremium) && (
            <div className="mt-3 p-3 bg-gray-50 rounded">
              <p className="text-sm font-medium">
                Total Premium: ${(parseFloat(data.employeePremium || 0) + parseFloat(data.employerPremium || 0)).toLocaleString()}/employee/month
              </p>
            </div>
          )}
        </div>

        <button onClick={compare} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700">
          Compare to Market Rates
        </button>
      </div>

      {results && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <p className="text-sm text-gray-500 mb-1">Current Annual Cost</p>
              <p className="text-2xl font-bold">${results.aimCost.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">${results.totalPremium}/employee/month</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <p className="text-sm text-gray-500 mb-1">Est. Market Alternative</p>
              <p className="text-2xl font-bold text-blue-600">${results.marketCost.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">${results.marketPremium}/employee/month</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-green-500">
              <p className="text-sm text-gray-500 mb-1">Potential Savings</p>
              <p className="text-2xl font-bold text-green-600">${results.savings.toLocaleString()}</p>
              <p className="text-xs text-green-600 mt-1">${results.monthlySavings.toLocaleString()}/month ({results.savingsPercent}% reduction)</p>
            </div>
          </div>

          {/* Export Actions */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold mb-4">Share This Analysis</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <button
                onClick={() => downloadPDF(results)}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Download PDF</span>
              </button>
              <button
                onClick={() => emailReport(results)}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>Email Report</span>
              </button>
              <button
                onClick={() => scheduleWebinarMeeting(results)}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                <span>Schedule Meeting</span>
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">Mark's Analysis for {results.municipality}</h3>
            <p className="text-gray-800">{results.recommendation}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold mb-3">What Could You Do With ${results.savings.toLocaleString()}?</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>Hire {Math.floor(results.savings / 50000)} new full-time employees at $50K/year</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>Fund infrastructure projects or equipment upgrades</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>Reduce property tax burden on residents</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>Build emergency fund reserves</span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const MedicareTransition = () => {
  const [data, setData] = useState({ currentPremium: '' });
  const [results, setResults] = useState(null);

  const calculate = () => {
    const prem = parseInt(data.currentPremium) || 800;
    const municipalityHRAContribution = 100;
    const typicalMedigapCost = 289;
    const employeeNetCost = municipalityHRAContribution + typicalMedigapCost;
    
    setResults({
      municipalitySavings: (prem - municipalityHRAContribution) * 12,
      employeeSavings: prem - employeeNetCost,
      municipalityMonthlyCost: municipalityHRAContribution,
      employeeMonthlyCost: employeeNetCost,
      recommendation: `I recommend Medicare + Medigap Plan G with a municipal HRA contribution of $${municipalityHRAContribution}/month. Employee pays ~$${employeeNetCost}/month total (vs $${prem} now), saving $${prem - employeeNetCost}/month. Municipality saves $${((prem - municipalityHRAContribution) * 12).toLocaleString()}/year per Medicare-eligible employee. Mark it, dude!`
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold mb-4">Medicare Transition Calculator</h2>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-indigo-900">
            <strong>Smart transition strategy:</strong> When employees hit 65, transitioning them to Medicare + Medigap saves both the municipality and the employee money.
          </p>
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">Current Monthly Premium (Employer Cost)</label>
          <input 
            type="number" 
            value={data.currentPremium} 
            onChange={(e) => setData({...data, currentPremium: e.target.value})} 
            placeholder="800" 
            className="w-full px-3 py-2 border rounded-md" 
          />
          <p className="text-xs text-gray-500 mt-1">What the municipality currently pays per Medicare-eligible employee</p>
        </div>
        <button onClick={calculate} className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700">
          Calculate Medicare Transition
        </button>
      </div>

      {results && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <p className="text-sm text-gray-500 mb-1">Annual Municipality Savings (per employee)</p>
              <p className="text-2xl font-bold text-green-600">${results.municipalitySavings.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-2">New cost: ${results.municipalityMonthlyCost}/month via HRA</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <p className="text-sm text-gray-500 mb-1">Employee Monthly Savings</p>
              <p className="text-2xl font-bold text-green-600">${results.employeeSavings}</p>
              <p className="text-xs text-gray-500 mt-2">New cost: ${results.employeeMonthlyCost}/month (Medicare + Medigap + Part D)</p>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
            <h3 className="font-semibold text-indigo-900 mb-2">Mark's Recommendation</h3>
            <p className="text-gray-800">{results.recommendation}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold mb-3">How It Works</h3>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3">
                  <span className="text-blue-600 font-bold">1</span>
                </div>
                <p><strong>Employee enrolls in Medicare Parts A & B</strong> (Part B costs ~$174/month in 2024, deducted from Social Security)</p>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3">
                  <span className="text-blue-600 font-bold">2</span>
                </div>
                <p><strong>Employee purchases Medigap Plan G</strong> (~$180/month) for comprehensive coverage</p>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3">
                  <span className="text-blue-600 font-bold">3</span>
                </div>
                <p><strong>Employee adds Part D prescription coverage</strong> (~$35/month)</p>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3">
                  <span className="text-blue-600 font-bold">4</span>
                </div>
                <p><strong>Municipality contributes to HRA</strong> (${results.municipalityMonthlyCost}/month) to help offset costs</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BudgetForecast = () => {
  const [data, setData] = useState({
    currentCost: '',
    employees: '',
    inflationRate: '5',
    years: '5'
  });
  const [forecast, setForecast] = useState(null);

  const generateForecast = () => {
    const currentCost = parseFloat(data.currentCost) || 1000;
    const employees = parseInt(data.employees) || 500;
    const inflationRate = parseFloat(data.inflationRate) / 100 || 0.05;
    const years = parseInt(data.years) || 5;

    const yearlyData = [];
    let cumulativeSavings = 0;

    for (let year = 1; year <= years; year++) {
      const inflatedCost = currentCost * Math.pow(1 + inflationRate, year);
      const totalCost = inflatedCost * employees * 12;
      const marketCost = totalCost * 0.87; // 13% savings
      const annualSavings = totalCost - marketCost;
      cumulativeSavings += annualSavings;

      yearlyData.push({
        year: new Date().getFullYear() + year,
        currentPath: Math.round(totalCost),
        marketPath: Math.round(marketCost),
        annualSavings: Math.round(annualSavings),
        cumulativeSavings: Math.round(cumulativeSavings),
        perEmployee: Math.round(inflatedCost)
      });
    }

    setForecast({ yearlyData, totalSavings: Math.round(cumulativeSavings) });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold mb-4">Budget Forecast Tool</h2>
        <p className="text-gray-600 mb-6">Project your benefits costs and potential savings over multiple years</p>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">Current Monthly Cost per Employee</label>
            <input
              type="number"
              value={data.currentCost}
              onChange={(e) => setData({ ...data, currentCost: e.target.value })}
              placeholder="1000"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Number of Employees</label>
            <input
              type="number"
              value={data.employees}
              onChange={(e) => setData({ ...data, employees: e.target.value })}
              placeholder="500"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Expected Annual Inflation Rate (%)</label>
            <input
              type="number"
              value={data.inflationRate}
              onChange={(e) => setData({ ...data, inflationRate: e.target.value })}
              placeholder="5"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Forecast Years</label>
            <input
              type="number"
              value={data.years}
              onChange={(e) => setData({ ...data, years: e.target.value })}
              placeholder="5"
              min="1"
              max="10"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
        </div>

        <button
          onClick={generateForecast}
          className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700"
        >
          Generate Forecast
        </button>
      </div>

      {forecast && (
        <div className="space-y-4">
          <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              Total {data.years}-Year Savings Potential
            </h3>
            <p className="text-4xl font-bold text-green-600">
              ${forecast.totalSavings.toLocaleString()}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold mb-4">Year-by-Year Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Year</th>
                    <th className="text-right py-2">Current Path</th>
                    <th className="text-right py-2">Market Alternative</th>
                    <th className="text-right py-2">Annual Savings</th>
                    <th className="text-right py-2">Cumulative Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.yearlyData.map((row, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-2">{row.year}</td>
                      <td className="text-right">${row.currentPath.toLocaleString()}</td>
                      <td className="text-right text-blue-600">${row.marketPath.toLocaleString()}</td>
                      <td className="text-right text-green-600">${row.annualSavings.toLocaleString()}</td>
                      <td className="text-right font-semibold text-green-700">
                        ${row.cumulativeSavings.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RFPGenerator = () => {
  const [data, setData] = useState({
    municipality: '',
    employees: '',
    currentCarrier: '',
    rfpDueDate: '',
    effectiveDate: '',
    contactName: '',
    contactEmail: ''
  });

  const generateRFP = () => {
    const rfpContent = `
REQUEST FOR PROPOSAL
Employee Group Health Insurance Benefits

${data.municipality || '[Municipality Name]'}

SUBMISSION DEADLINE: ${data.rfpDueDate || '[Date]'}
EFFECTIVE DATE: ${data.effectiveDate || '[Date]'}

SECTION 1: MUNICIPALITY INFORMATION
Municipality: ${data.municipality || '[Municipality Name]'}
Number of Eligible Employees: ${data.employees || '[Number]'}
Current Carrier: ${data.currentCarrier || '[Carrier Name]'}

Contact Person: ${data.contactName || '[Name]'}
Email: ${data.contactEmail || '[Email]'}

SECTION 2: SCOPE OF SERVICES
The municipality is seeking proposals from qualified insurance carriers and third-party administrators to provide comprehensive group health insurance coverage for eligible employees and their dependents.

SECTION 3: COVERAGE REQUIREMENTS
Required Coverage Types:
- Medical Insurance (PPO and/or HDHP options)
- Prescription Drug Coverage
- Dental Insurance
- Vision Insurance
- Life Insurance and AD&D
- Short-term and Long-term Disability

SECTION 4: PLAN DESIGN PARAMETERS
The municipality seeks competitive plan designs that:
- Maintain or improve current benefit levels
- Offer multiple plan options for employee choice
- Include preventive care at 100% coverage
- Provide in-network and out-of-network benefits
- Include mail order and retail pharmacy options

SECTION 5: CENSUS AND UTILIZATION DATA
Detailed employee census and claims history will be provided to qualified bidders upon execution of a confidentiality agreement.

Current Annual Premium: Approximately $${data.employees ? (parseInt(data.employees) * 1000 * 12).toLocaleString() : '[Amount]'}

SECTION 6: PROPOSAL REQUIREMENTS
Respondents must provide:
1. Company background and qualifications
2. Proposed plan designs (minimum 2 medical options)
3. Premium rates by tier (Employee, Employee+Spouse, Employee+Child(ren), Family)
4. Network information and provider access
5. Implementation timeline and support services
6. Claims processing and customer service procedures
7. Wellness program offerings
8. Technology platforms and member portals
9. Reporting capabilities
10. References from similar-sized municipal clients

SECTION 7: EVALUATION CRITERIA
Proposals will be evaluated based on:
- Cost competitiveness (40%)
- Plan design and benefit quality (25%)
- Network adequacy and provider access (15%)
- Service capabilities and technology (10%)
- Implementation support (5%)
- References and experience with municipalities (5%)

SECTION 8: SUBMISSION INSTRUCTIONS
Submit proposals electronically to: ${data.contactEmail || '[Email]'}
Deadline: ${data.rfpDueDate || '[Date]'} by 5:00 PM EST

SECTION 9: TIMELINE
RFP Release: ${new Date().toLocaleDateString()}
Questions Due: [2 weeks from release]
Proposals Due: ${data.rfpDueDate || '[Date]'}
Finalist Presentations: [1 week after proposals]
Board Decision: [2 weeks after presentations]
Contract Execution: [Date]
Implementation Period: [Date Range]
Effective Date: ${data.effectiveDate || '[Date]'}

SECTION 10: TERMS AND CONDITIONS
- The municipality reserves the right to reject any or all proposals
- The municipality reserves the right to negotiate with selected finalists
- All proposals become property of the municipality
- Confidentiality of employee data must be maintained

For questions, contact:
${data.contactName || '[Name]'}
${data.contactEmail || '[Email]'}

Generated by Mark - Municipal Benefits Optimization Tool
    `;

    const blob = new Blob([rfpContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.municipality.replace(/\s+/g, '-') || 'Municipality'}-Benefits-RFP-${new Date().getFullYear()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold mb-4">RFP Generator</h2>
        <p className="text-gray-600 mb-6">
          Generate a professional Request for Proposal for employee benefits
        </p>

        <div className="space-y-4 mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Municipality Name</label>
              <input
                type="text"
                value={data.municipality}
                onChange={(e) => setData({ ...data, municipality: e.target.value })}
                placeholder="City of Indianapolis"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Number of Employees</label>
              <input
                type="number"
                value={data.employees}
                onChange={(e) => setData({ ...data, employees: e.target.value })}
                placeholder="500"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Current Carrier (Optional)</label>
            <input
              type="text"
              value={data.currentCarrier}
              onChange={(e) => setData({ ...data, currentCarrier: e.target.value })}
              placeholder="AIM Trust"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">RFP Due Date</label>
              <input
                type="date"
                value={data.rfpDueDate}
                onChange={(e) => setData({ ...data, rfpDueDate: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Desired Effective Date</label>
              <input
                type="date"
                value={data.effectiveDate}
                onChange={(e) => setData({ ...data, effectiveDate: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Contact Name</label>
              <input
                type="text"
                value={data.contactName}
                onChange={(e) => setData({ ...data, contactName: e.target.value })}
                placeholder="Jane Smith"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Email</label>
              <input
                type="email"
                value={data.contactEmail}
                onChange={(e) => setData({ ...data, contactEmail: e.target.value })}
                placeholder="jsmith@city.gov"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>
        </div>

        <button
          onClick={generateRFP}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 flex items-center justify-center space-x-2"
        >
          <Download className="w-5 h-5" />
          <span>Generate & Download RFP</span>
        </button>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> This generates a comprehensive RFP template. Review and customize it to match your specific requirements before distribution.
          </p>
        </div>
      </div>
    </div>
  );
};

const EnrollmentTimeline = () => {
  const [effectiveDate, setEffectiveDate] = useState('');
  const [timeline, setTimeline] = useState(null);

  const generateTimeline = () => {
    if (!effectiveDate) {
      alert('Please select an effective date');
      return;
    }

    const effective = new Date(effectiveDate);
    
    const milestones = [
      {
        weeks: -24,
        phase: 'Planning',
        task: 'Begin RFP Process',
        description: 'Start gathering requirements and preparing RFP documentation',
        owner: 'HR/Benefits Team'
      },
      {
        weeks: -20,
        phase: 'Planning',
        task: 'Issue RFP',
        description: 'Distribute RFP to qualified carriers and brokers',
        owner: 'HR Director'
      },
      {
        weeks: -16,
        phase: 'Selection',
        task: 'RFP Responses Due',
        description: 'Review and evaluate all submitted proposals',
        owner: 'Selection Committee'
      },
      {
        weeks: -14,
        phase: 'Selection',
        task: 'Finalist Presentations',
        description: 'Schedule and conduct finalist presentations',
        owner: 'Selection Committee'
      },
      {
        weeks: -12,
        phase: 'Selection',
        task: 'Vendor Selection',
        description: 'Board approval and contract negotiation',
        owner: 'City Council'
      },
      {
        weeks: -10,
        phase: 'Implementation',
        task: 'Contract Execution',
        description: 'Sign agreements and begin implementation planning',
        owner: 'City Manager'
      },
      {
        weeks: -8,
        phase: 'Implementation',
        task: 'Plan Design Finalization',
        description: 'Finalize all plan documents and materials',
        owner: 'HR/Carrier'
      },
      {
        weeks: -6,
        phase: 'Communication',
        task: 'Employee Communication Plan',
        description: 'Develop communication strategy and materials',
        owner: 'HR/Communications'
      },
      {
        weeks: -5,
        phase: 'Communication',
        task: 'Enrollment Materials Ready',
        description: 'All enrollment guides, forms, and materials prepared',
        owner: 'HR/Carrier'
      },
      {
        weeks: -4,
        phase: 'Communication',
        task: 'Employee Meetings Begin',
        description: 'Host informational sessions and benefits fairs',
        owner: 'HR Team'
      },
      {
        weeks: -3,
        phase: 'Enrollment',
        task: 'Open Enrollment Begins',
        description: 'Launch enrollment system and support helpline',
        owner: 'HR/Carrier'
      },
      {
        weeks: -1,
        phase: 'Enrollment',
        task: 'Open Enrollment Ends',
        description: 'Final day for employee elections',
        owner: 'HR Team'
      },
      {
        weeks: 0,
        phase: 'Go-Live',
        task: 'Coverage Effective Date',
        description: 'New benefits coverage begins',
        owner: 'All'
      },
      {
        weeks: 2,
        phase: 'Post-Launch',
        task: 'Post-Enrollment Support',
        description: 'Address questions and resolve issues',
        owner: 'HR/Carrier'
      }
    ];

    const timelineWithDates = milestones.map(milestone => {
      const date = new Date(effective);
      date.setDate(date.getDate() + (milestone.weeks * 7));
      return {
        ...milestone,
        date: date.toLocaleDateString(),
        isPast: date < new Date(),
        isToday: date.toDateString() === new Date().toDateString()
      };
    });

    setTimeline(timelineWithDates);
  };

  const getPhaseColor = (phase) => {
    const colors = {
      'Planning': 'bg-blue-100 text-blue-800',
      'Selection': 'bg-purple-100 text-purple-800',
      'Implementation': 'bg-yellow-100 text-yellow-800',
      'Communication': 'bg-green-100 text-green-800',
      'Enrollment': 'bg-indigo-100 text-indigo-800',
      'Go-Live': 'bg-red-100 text-red-800',
      'Post-Launch': 'bg-gray-100 text-gray-800'
    };
    return colors[phase] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold mb-4">Benefits Enrollment Timeline</h2>
        <p className="text-gray-600 mb-6">
          Generate a comprehensive timeline for your benefits transition and enrollment
        </p>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">Coverage Effective Date</label>
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <button
          onClick={generateTimeline}
          className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700"
        >
          Generate Timeline
        </button>
      </div>

      {timeline && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-semibold mb-4">Implementation Timeline</h3>
          <p className="text-sm text-gray-600 mb-6">
            Working backward from effective date: {effectiveDate}
          </p>

          <div className="space-y-4">
            {timeline.map((milestone, idx) => (
              <div
                key={idx}
                className={`border-l-4 pl-4 py-3 ${
                  milestone.isToday
                    ? 'border-green-500 bg-green-50'
                    : milestone.isPast
                    ? 'border-gray-300 bg-gray-50'
                    : 'border-blue-500 bg-blue-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`text-xs px-2 py-1 rounded ${getPhaseColor(milestone.phase)}`}>
                        {milestone.phase}
                      </span>
                      <span className="text-sm text-gray-600">{milestone.date}</span>
                      {milestone.isToday && (
                        <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">Today</span>
                      )}
                    </div>
                    <h4 className="font-semibold text-gray-900">{milestone.task}</h4>
                    <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                    <p className="text-xs text-gray-500 mt-1">Owner: {milestone.owner}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Timeline Tips</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Start the RFP process at least 6 months before desired effective date</li>
              <li>• Allow 3-4 weeks for open enrollment</li>
              <li>• Schedule employee meetings during enrollment period</li>
              <li>• Plan for post-enrollment support in first month</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkApp;
